import React from "react";
import { motion } from "motion/react";
import {
  Users,
  Target,
  Lightbulb,
  Globe,
  Award,
  Briefcase,
  Beaker,
  Calendar,
  Rocket,
  Building,
  GraduationCap,
  BookOpen,
  Sparkles,
  TrendingUp,
  Map,
} from "lucide-react";
import { Card } from "../ui/card";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { ThreeDTeamCarousel } from "../ThreeDTeamCarousel";

interface TeamMemberProps {
  name: string;
  role?: string;
  image: string;
  bio: string;
  delay?: number;
  specialtyIcon?: React.ReactNode;
}

// Industry Pioneer Card with Orbital/Radial Design
function TeamMember({
  name,
  role,
  image,
  bio,
  delay = 0,
  specialtyIcon,
}: TeamMemberProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.7, delay }}
      viewport={{ once: true }}
      className="group w-full h-full"
    >
      <Card className="relative overflow-hidden h-full bg-gradient-to-br from-primary/5 via-background/80 to-secondary/5 border-2 border-primary/20 hover:border-primary/50 transition-all duration-500">
        {/* Radial gradient overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(0,207,255,0.1),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Animated corner sparkles */}
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -top-6 -right-6 w-20 h-20 bg-gradient-to-br from-primary/30 to-accent/30 rounded-full blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-500"
        />

        <div className="relative p-6 sm:p-8">
          {/* Circular image with orbital ring effect */}
          <div className="relative mb-6 flex justify-center">
            <motion.div
              whileHover={{ rotate: 5 }}
              transition={{ duration: 0.3 }}
              className="relative"
            >
              {/* Outer orbital ring */}
              <div className="absolute inset-0 -m-3 rounded-full border-2 border-dashed border-primary/30 group-hover:border-primary/60 transition-colors duration-500" />

              {/* Glowing background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-full blur-lg opacity-40 group-hover:opacity-70 transition-opacity duration-500" />

              {/* Image container */}
              <div className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-full overflow-hidden border-4 border-background shadow-2xl">
                <ImageWithFallback
                  src={image}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Floating badge */}
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{
                  delay: delay + 0.3,
                  type: "spring",
                }}
                className="absolute -bottom-2 -right-2 bg-gradient-to-r from-secondary to-primary p-2 rounded-full shadow-lg"
              >
                {specialtyIcon || <Sparkles className="w-5 h-5 text-background" />}
              </motion.div>
            </motion.div>
          </div>

          {/* Content */}
          <div className="text-center space-y-3">
            <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300">
              {name}
            </h3>
            {role && (
              <div className="flex items-center justify-center gap-2 text-primary/80">
                <TrendingUp className="w-4 h-4" />
                <p className="font-semibold text-sm sm:text-base">
                  {role}
                </p>
              </div>
            )}
            <div className="pt-2 border-t border-primary/20">
              <p className="text-muted-foreground text-sm leading-relaxed text-justify">
                {bio}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

interface LeadershipMemberProps {
  name: string;
  title: string;
  image: string;
  currentRoles: string;
  previousLeadership?: string;
  previousExperience?: string;
  mentorshipImpact?: string;
  delay?: number;
}

// Leadership Card with Premium Hexagonal/Diamond Design
function LeadershipMember({
  name,
  title,
  image,
  currentRoles,
  previousLeadership,
  previousExperience,
  mentorshipImpact,
  delay = 0,
}: LeadershipMemberProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -30, rotateY: -10 }}
      whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.8, delay }}
      viewport={{ once: true }}
      className="group perspective-1000 h-full"
    >
      <Card className="relative overflow-hidden h-full flex flex-col bg-gradient-to-br from-secondary/10 via-background to-primary/5 border-2 border-transparent hover:border-secondary/60 transition-all duration-500 shadow-2xl">
        {/* Animated diagonal stripes background */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(138, 63, 252, 0.05) 10px,
              rgba(138, 63, 252, 0.05) 20px
            )`,
          }}
        />

        {/* Premium corner decorations */}
        <div className="absolute top-0 left-0 w-24 h-24 border-t-4 border-l-4 border-secondary/40 group-hover:border-secondary transition-colors duration-500" />
        <div className="absolute bottom-0 right-0 w-24 h-24 border-b-4 border-r-4 border-primary/40 group-hover:border-primary transition-colors duration-500" />

        {/* Glowing accent line */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          transition={{ delay: delay + 0.3, duration: 0.8 }}
          className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent origin-left"
        />

        <div className="relative p-5 flex flex-col h-full">
          {/* Circular image */}
          <div className="relative mb-5 flex items-center justify-center flex-shrink-0">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.4, type: "spring" }}
              className="relative w-24 h-24"
            >
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-secondary via-primary to-accent p-[2px]">
                <div className="w-full h-full bg-background rounded-full" />
              </div>
              <div className="relative w-full h-full overflow-hidden rounded-full border-2 border-background shadow-lg">
                <ImageWithFallback
                  src={image}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>

          {/* Header */}
          <div className="text-center mb-4 flex-shrink-0">
            <h3 className="text-base font-bold mb-1 bg-gradient-to-r from-secondary via-primary to-accent bg-clip-text text-transparent">
              {name}
            </h3>
            <p className="text-secondary font-semibold text-xs text-center">{title}</p>
          </div>

          {/* Content sections */}
          <div className="space-y-3 flex-grow">
            <div className="relative pl-4 border-l-2 border-secondary/50">
              <div className="absolute -left-[5px] top-1 w-2.5 h-2.5 bg-secondary rounded-full" />
              <h4 className="font-semibold text-secondary text-xs mb-1">Current Roles</h4>
              <p className="text-muted-foreground text-xs leading-relaxed text-left">{currentRoles}</p>
            </div>

            {previousLeadership && (
              <div className="relative pl-4 border-l-2 border-primary/50 hidden">
                <div className="absolute -left-[5px] top-1 w-2.5 h-2.5 bg-primary rounded-full" />
                <h4 className="font-semibold text-primary text-xs mb-1">Previous Leadership</h4>
                <p className="text-muted-foreground text-xs leading-relaxed text-left">{previousLeadership}</p>
              </div>
            )}

            {previousExperience && (
              <div className="relative pl-4 border-l-2 border-accent/50">
                <div className="absolute -left-[5px] top-1 w-2.5 h-2.5 bg-accent rounded-full" />
                <h4 className="font-semibold text-accent text-xs mb-1">Previous Experience</h4>
                <p className="text-muted-foreground text-xs leading-relaxed text-left">{previousExperience}</p>
              </div>
            )}

            {mentorshipImpact && (
              <div className="relative pl-4 border-l-2 border-secondary/50">
                <div className="absolute -left-[5px] top-1 w-2.5 h-2.5 bg-secondary rounded-full" />
                <h4 className="font-semibold text-secondary text-xs mb-1">Mentorship & Impact</h4>
                <p className="text-muted-foreground text-xs leading-relaxed text-left">{mentorshipImpact}</p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

interface JourneyMilestoneProps {
  year: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  index: number;
}

function JourneyMilestone({
  year,
  title,
  description,
  icon,
  index,
}: JourneyMilestoneProps) {
  const isEven = index % 2 === 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: isEven ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: isEven ? -50 : 50 }}
      transition={{ duration: 0.8, delay: index * 0.2 }}
      viewport={{ once: true }}
      className={`relative flex items-center mb-16 ${isEven
        ? "md:justify-start justify-center"
        : "md:justify-end justify-center"
        }`}
    >
      <div
        className={`w-full md:w-1/2 ${isEven ? "md:pr-12 md:text-right" : "md:pl-12"} px-4 md:px-0`}
      >
        <motion.div
          whileHover={{ y: -10, scale: 1.02 }}
          transition={{ duration: 0.3 }}
          className="relative"
        >
          <Card
            className="bg-[#111111] border-border/30 hover:border-primary/50 transition-all duration-500 relative overflow-hidden p-6 md:p-8"
            style={{
              background: "#111111",
              backdropFilter: "none",
            }}
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primary/20 to-accent/20 rounded-bl-full" />

            {/* Mobile timeline dot - positioned at top */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{
                duration: 0.5,
                delay: index * 0.2 + 0.3,
              }}
              viewport={{ once: true }}
              className="md:hidden absolute -top-3 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-gradient-to-r from-primary to-accent rounded-full border-4 border-background z-10"
            />

            {/* Mobile glowing effect */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{
                duration: 0.5,
                delay: index * 0.2 + 0.5,
              }}
              viewport={{ once: true }}
              className="md:hidden absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-primary/20 rounded-full animate-pulse"
            />

            <div
              className={`flex items-center mb-4 mt-6 md:mt-0 ${isEven ? "md:justify-end justify-center" : "md:justify-start justify-center"}`}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center text-black mr-4">
                {icon}
              </div>
              <div className="text-primary font-bold text-2xl">
                {year}
              </div>
            </div>

            <div
              className={`${isEven ? "md:text-right text-center" : "md:text-left text-center"}`}
            >
              <h3
                className={`text-xl font-bold mb-3 ${isEven ? "md:text-right text-center" : "md:text-left text-center"}`}
              >
                {title}
              </h3>
              <p
                className={`text-muted-foreground leading-relaxed ${isEven ? "md:text-right text-justify" : "md:text-left text-justify"}`}
              >
                {description}
              </p>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Desktop timeline dot - positioned at center */}
      <motion.div
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
        viewport={{ once: true }}
        className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-gradient-to-r from-primary to-accent rounded-full border-4 border-background z-10"
      />

      {/* Desktop glowing effect */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: index * 0.2 + 0.5 }}
        viewport={{ once: true }}
        className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-primary/20 rounded-full animate-pulse"
      />
    </motion.div>
  );
}

interface IndustryPioneersSliderProps {
  pioneers: Array<{
    name: string;
    title: string;
    image: string;
    bio: string;
  }>;
}

function IndustryPioneersSlider({
  pioneers,
}: IndustryPioneersSliderProps) {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [isAutoPlaying, setIsAutoPlaying] =
    React.useState(true);
  const itemsPerSlide = 3;
  const totalSlides = Math.ceil(
    pioneers.length / itemsPerSlide,
  );

  // Auto-slide effect
  React.useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 5 seconds of manual interaction
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const currentPioneers = pioneers.slice(
    currentSlide * itemsPerSlide,
    (currentSlide + 1) * itemsPerSlide,
  );

  return (
    <div className="relative max-w-7xl mx-auto">
      {/* Slider container */}
      <div className="overflow-hidden">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap justify-center gap-8 items-stretch"
        >
          {currentPioneers.map((pioneer, index) => (
            <div
              key={`${pioneer.name}-${currentSlide}-${index}`}
              className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] max-w-md flex"
            >
              <TeamMember {...pioneer} delay={index * 0.1} />
            </div>
          ))}
        </motion.div>
      </div>

      {/* Pagination dots */}
      <div className="flex justify-center items-center gap-3 mt-12">
        {Array.from({ length: totalSlides }).map((_, index) => (
          <motion.button
            key={index}
            onClick={() => goToSlide(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            className={`rounded-full transition-all duration-300 ${currentSlide === index
              ? "w-12 h-3 bg-gradient-to-r from-primary to-secondary"
              : "w-3 h-3 bg-muted-foreground/30 hover:bg-muted-foreground/50"
              }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Slide counter */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center mt-4 text-sm text-muted-foreground"
      >
        {currentSlide + 1} / {totalSlides}
      </motion.div>
    </div>
  );
}

// Academic Pioneers Showcase with Vertical Scrolling List
interface AcademicPioneersShowcaseProps {
  pioneers: Array<{
    name: string;
    degree: string;
    institution: string;
    experience: string;
    image: string;
    bio: string;
  }>;
}

function AcademicPioneersShowcase({
  pioneers,
}: AcademicPioneersShowcaseProps) {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isAutoPlaying, setIsAutoPlaying] =
    React.useState(true);
  const leftCardRef = React.useRef < HTMLDivElement > (null);
  const listContainerRef = React.useRef < HTMLDivElement > (null);
  const itemHeight = 88; // Height of each list item (py-4 + content)

  // Auto-scroll effect
  React.useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % pioneers.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, pioneers.length]);

  const handlePioneerClick = (index: number) => {
    setActiveIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 6 seconds
    setTimeout(() => setIsAutoPlaying(true), 6000);
  };

  const activePioneer = pioneers[activeIndex];

  return (
    <div className="max-w-7xl mx-auto">
      <Card className="relative overflow-hidden bg-gradient-to-br from-secondary/10 via-background/50 to-accent/10 border-2 border-accent/30 hover:border-accent/50 transition-all duration-500">
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-accent/5 to-secondary/0 opacity-50" />

        {/* Corner accents */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-accent/20 to-transparent" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-primary/20 to-transparent" />

        <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
          {/* Left Side - Active Pioneer Details */}
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="order-2 lg:order-1 relative"
            ref={leftCardRef}
          >
            <div className="relative p-8 space-y-6 h-full">
              {/* Image */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="relative w-48 h-48 mx-auto"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-accent via-primary to-secondary rounded-2xl blur-xl opacity-50" />
                <div className="relative w-full h-full rounded-2xl overflow-hidden border-4 border-accent/40 shadow-2xl">
                  <ImageWithFallback
                    src={activePioneer.image}
                    alt={activePioneer.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Floating badge */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring" }}
                  className="absolute -bottom-3 -right-3 bg-gradient-to-r from-accent to-primary text-background px-4 py-2 rounded-full shadow-lg"
                >
                  <GraduationCap className="w-5 h-5 inline mr-2" />
                  <span className="font-bold text-sm">
                    {activePioneer.degree}
                  </span>
                </motion.div>
              </motion.div>

              {/* Name and Title */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-center space-y-2"
              >
                <h3 className="text-3xl font-bold bg-gradient-to-r from-accent via-primary to-secondary bg-clip-text text-transparent">
                  {activePioneer.name}
                </h3>
                <div className="flex items-center justify-center gap-2 text-accent">
                  <Building className="w-5 h-5" />
                  <p className="font-semibold">
                    {activePioneer.institution}
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 text-primary/80">
                  <Briefcase className="w-4 h-4" />
                  <p className="text-sm">
                    {activePioneer.experience}
                  </p>
                </div>
              </motion.div>

              {/* Divider */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="h-px bg-gradient-to-r from-transparent via-accent/50 to-transparent"
              />

              {/* Bio */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="space-y-3"
              >
                <div className="flex items-center gap-2 text-accent">
                  <BookOpen className="w-5 h-5" />
                  <h4 className="font-bold">Biography</h4>
                </div>
                <p className="text-muted-foreground leading-relaxed text-left">
                  {activePioneer.bio}
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* Vertical Divider - only visible on desktop */}
          <div className="hidden lg:block absolute left-1/2 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-accent/50 to-transparent transform -translate-x-1/2" />

          {/* Right Side - Scrolling Names List */}
          <div
            className="order-1 lg:order-2 relative"
            ref={listContainerRef}
          >
            {/* Animated top gradient fade */}
            <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-background/50 via-transparent to-transparent z-10 pointer-events-none" />
            {/* Animated bottom gradient fade */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background/50 via-transparent to-transparent z-10 pointer-events-none" />

            {/* Scrolling container with visual animation */}
            <div className="relative py-8 px-4 h-full">
              <div
                className="space-y-2 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                style={{ maxHeight: `${5 * itemHeight}px` }}
              >
                {pioneers.map((pioneer, listIndex) => {
                  const isActive = pioneer === activePioneer;
                  return (
                    <motion.button
                      key={listIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.3,
                        ease: "easeOut",
                        delay: listIndex * 0.05,
                      }}
                      onClick={() =>
                        handlePioneerClick(listIndex)
                      }
                      className={`
                          w-full px-6 py-4 text-left transition-all duration-500 relative block
                          ${isActive
                          ? "bg-gradient-to-r from-accent/20 via-primary/10 to-transparent border-l-4 border-accent scale-105"
                          : "hover:bg-primary/5 border-l-4 border-transparent hover:border-primary/30"
                        }
                        `}
                    >
                      {/* Active indicator glow */}
                      {isActive && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="absolute inset-0 bg-gradient-to-r from-accent/10 to-transparent"
                        />
                      )}

                      <div className="relative flex items-center gap-4">
                        {/* Small circular photo */}
                        <div
                          className={`
                            relative flex-shrink-0 rounded-full overflow-hidden border-2 transition-all duration-500
                            ${isActive
                              ? "w-16 h-16 border-accent shadow-lg shadow-accent/30"
                              : "w-12 h-12 border-primary/30"
                            }
                          `}
                        >
                          <ImageWithFallback
                            src={pioneer.image}
                            alt={pioneer.name}
                            className="w-full h-full object-cover"
                          />
                          {isActive && (
                            <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-transparent" />
                          )}
                        </div>

                        {/* Name and title */}
                        <div className="flex-1 min-w-0">
                          <h4
                            className={`
                              font-bold transition-all duration-500 truncate
                              ${isActive
                                ? "text-lg bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent"
                                : "text-base text-foreground"
                              }
                            `}
                          >
                            {pioneer.name}
                          </h4>
                          <p
                            className={`
                              text-sm transition-all duration-500 truncate
                              ${isActive ? "text-accent/80" : "text-muted-foreground"}
                            `}
                          >
                            {pioneer.institution}
                          </p>
                        </div>

                        {/* Active indicator icon */}
                        {isActive && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                              type: "spring",
                              stiffness: 200,
                            }}
                          >
                            <GraduationCap className="w-5 h-5 text-accent" />
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Progress indicator */}
            <div className="absolute bottom-2 right-4 text-xs text-muted-foreground font-mono bg-background/80 px-2 py-1 rounded">
              {activeIndex + 1} / {pioneers.length}
            </div>

            {/* Auto-play indicator */}

          </div>
        </div>
      </Card>
    </div>
  );
}

export function AboutPage() {
  const journeyMilestones = [
    {
      year: "2020",
      title: "Foundation & Vision",
      description:
        "amasQIS.ai was founded with a vision to make AI technology accessible to everyone. Our journey began with a small team of passionate AI researchers and engineers.",
      icon: <Rocket className="w-6 h-6" />,
    },
    {
      year: "2021",
      title: "First AI Breakthrough",
      description:
        "Launched our first predictive maintenance platform, revolutionizing how industries approach equipment monitoring and failure prediction.",
      icon: <Target className="w-6 h-6" />,
    },
    {
      year: "2022",
      title: "Global Expansion",
      description:
        "Expanded operations internationally with offices in UK, US, UAE, Oman, Germany, Malaysia, and Singapore, bringing AI solutions to diverse markets.",
      icon: <Globe className="w-6 h-6" />,
    },
    {
      year: "2023",
      title: "Industry Recognition",
      description:
        "Achieved significant milestones in Computer Vision and Bioinformatics, earning recognition as leaders in specialized AI applications.",
      icon: <Award className="w-6 h-6" />,
    },
    {
      year: "2024",
      title: "Innovation Leadership",
      description:
        "Pioneered breakthrough technologies in Green Hydrogen projects and sustainable AI solutions, setting new industry standards.",
      icon: <Building className="w-6 h-6" />,
    },
    {
      year: "2025",
      title: "Future Vision",
      description:
        "Continuing our mission to become a unicorn in the AI space, driving sustainable and impactful change across key global sectors.",
      icon: <Lightbulb className="w-6 h-6" />,
    },
  ];

  const leadershipByBranch: Record<string, { name: string; title: string; image: string; currentRoles: string; previousExperience: string; previousLeadership?: string }[]> = {
    india: [
      {
        name: "Mr. Sandeep Kunda",
        title: "Managing Director",
        image: "/assets/leadership/sandeep.png",
        currentRoles:
          "Leads the strategic direction, business operations, and growth of amasQIS.ai. He will drive AI product innovation, build strategic partnerships, strengthen market presence, and guide teams in delivering scalable, responsible, and high-impact AI solutions.",
         },
      {
        name: "Dr. Mayilvelnathan \u00A0",
        title: "Head of the Advisory Board",
        image: "/assets/leadership/mayilvelnathan.png",
        currentRoles:
          "Dr. Mayilvelnathan provides strategic and technical guidance for amasQIS.ai’s green hydrogen and sustainable energy initiatives. He will support long-term planning, technology development, industry collaboration, and the company’s contribution to the global hydrogen economy.",
        },
      {
        name: "Mr. Nagarajan Chenniyappan",
        title: "Director - Head of Finance",
        image: "/assets/leadership/NagarajanChenniyappan.png",
        currentRoles:
          "Mr. Nagarajan Chenniyappan leads financial strategy, budgeting, investment planning, and long-term financial management at amasQIS.ai. He will help secure funding through grants, investors, and strategic partnerships while supporting the company’s research, innovation, and sustainable growth.",
         },
      {
        name: "Mr. Gajendran",
        title: "Senior Director – Projects",
        image: "/assets/leadership/Gajendran.jpeg",
        currentRoles:
          "Mr. Gajendran leads the planning and execution of amasQIS.ai’s infrastructure and engineering projects. He will oversee project schedules, contracts, materials, costs, site activities, and coordination to ensure successful delivery from planning through completion.",
      }
    ],
    oman: [
      {
        name: "Mr. Saud",
        title: "Managing Director",
        image: "/assets/leadership/placeholder.png",
        currentRoles:
          "Leads the strategic direction, business growth, and market development of amasQIS.ai Oman. He will build key partnerships, drive AI-led innovation, strengthen the company’s regional presence, and guide the team in delivering scalable and high-impact technology solutions.",
        },
      {
        name: "Mr. Aanand",
        title: "General Manager",
        image: "/assets/leadership/default.png",
        currentRoles:
          "Oversees the day-to-day operations and execution of amasQIS.ai Oman’s business strategy. He will coordinate cross-functional teams, manage projects and resources, improve operational efficiency, and ensure the successful delivery of the company’s AI solutions and business objectives.",
      },
    ],
    uk: [
      {
        name: "Banu Thiagaraj",
        title: "Director",
        image: "/assets/leadership/Banu_Thiagaraj.jpeg",
        currentRoles:
          "Leads business development and market expansion for amasQIS.ai in the United Kingdom. She will build strategic partnerships, develop client relationships, identify new commercial opportunities, and drive the adoption of the company’s AI solutions across key industries.",
       },
    ],
    dubai: [
      // {
      //   name: "Mr.Abdhllah khalifa Khalaf al Shaaibi",
      //   title: "Managing Director",
      //   image: "/assets/leadership/default.png",
      //   currentRoles:
      //     "As Managing Director at amasQIS.ai Dubai, he provides strategic leadership and drives innovation across regional initiatives. With his military background, he brings disciplined execution and strategic thinking to deliver impactful, industry-ready solutions that strengthen capability and support regional growth.",
      //   previousLeadership:
      //     "With extensive military experience, he led organizations through strategic transitions and operational excellence. Contributed to securing key partnerships and supporting strategic expansion in emerging technology sectors.",
      //   previousExperience:
      //     "Ex-Army officer with proven expertise in leadership, strategic planning, and operational management. Known for delivering high-quality solutions and improving organizational performance across complex, mission-critical projects.",
      // },
    ],
  };

  const industryPioneers = [
    {
      name: "Dr. R. Vijaya Kumar",
      role: "",
      title: "GIS Expert",
      image: "/assets/leadership/placeholder-1.png",
      bio: "A seasoned professional with over 25 years of experience in ITES and IT, including a decade in GIS for telecom projects and 15 years in software development. Skilled in image-to-vector conversion for US and UK clients, and proficient in system analysis, architecture, and full-stack development. Has led enterprise projects across healthcare, retail, telecom, and e-commerce, progressing from Developer to Technical Lead.",
      specialtyIcon: <Map className="w-5 h-5 text-background" />,
    },
  ];
  const academicPioneers = [
       {
      name: "Dr. M. Sumathi",
      degree: "Ph.D.",
      institution:
        "Managing Director - Academic Division",
      experience: "20 years in Academia",
      image: "/assets/leadership/Mrs_sumathi.jpeg",
      bio: "Dr. M. Sumathi is an experienced academician and researcher serving as the Head of the Department of Research and PG Department of Computer Science at Chithode, Erode. She has 22 years of teaching experience and 20 years of research experience in the field of Computer Science. She has presented 15 national and 10 international research papers, attended 20 Faculty Development Programmes, and participated in more than 50 national and international conferences. She has also published a book titled “Elliptic Curve Crypto Techniques for Secure Communication.” In addition to her academic contributions, she is guiding four ongoing Ph.D. research scholars and has supervised more than 50 M.Phil. scholars. She also serves as the State Vice President of Mission New India, Tamil Nadu, and is a member of the We the Leaders NGO, Tamil Nadu. Her extensive experience in teaching, research, academic mentoring, and professional activities reflects her commitment to education and research.",
    },
        {
      name: "M. Ramaswamy",
      degree: "Ph.D.",
      institution: "Former Technical Expert, Levels Training Institute, Oman",
      experience: "42 years of experiences in various areas of Engineering",
      image: "/assets/leadership/Ramaswamy.jpeg",
      bio: "M. Ramaswamy earned Ph.D. degree from Indian Institute of Technology, Delhi, India in Mechanical Engineering. He got over 42 years of combined extensive experiences in various areas of Engineering in India and Oman. His last assignment was a Technical Expert to Government of Oman. He has been working in Oman since 1997. Before coming to Oman he worked in Government of India in Delhi for 12 years. He has designed few of largest utility plants in South East Asia. He has published more than 170 research papers in international journals and conferences and authored six books on engineering subjects.",
    },
    {
      name: "Dr. R. Sankarasubramanian",
      degree: "Ph.D.",
      institution: "Principal, Erode Arts and Science College",
      experience: "31+ years in Academia",
      image: "/assets/leadership/Mr_sankarasubramaniyan.png",
      bio: "With over 31 years of academic experience, he has played a pivotal role in shaping computer science education and research. Currently serving as the Principal of Erode Arts and Science College, Tamil Nadu, he is a double doctorate holder and research guide, having mentored numerous M.Phil and Ph.D. scholars. His expertise in network security, software testing, data mining, and image processing supports our vision of innovation and excellence.",
    },
    
    // {
    //   name: "Dr. P. Kamaraj",
    //   degree: "Ph.D.",
    //   institution: "Principal, Karuppannan Mariappan College",
    //   experience: "15+ years of teaching experience",
    //   image: "/assets/leadership/default.png",
    //   bio: "Dr. P. Kamaraj is the Principal of Karuppannan Mariappan College, leading academics, administration, and holistic student development. He has over 15 years of teaching experience and has served as Dean – Academic (Commerce & Management) at Vivekanandha College of Arts and Sciences for Women, Tiruchengode. He holds a Ph.D. in Management (Highly Commended) from Gandhigram Rural University and multiple first-class degrees, along with clearing the SET in 2006. He also manages a 20-acre sustainable farm, actively engages with rural communities, secures institutional grants, and promotes rural-inclusive, employability-focused higher education.",
    // },
    {
      name: "Dr. L. Velmurugan",
      degree: "Ph.D.",
      institution: "Associate Professor, VIT Bhopal University",
      experience: "26+ years in Academia",
      image: "/assets/leadership/Dr_velmurugan.png",
      bio: "With 26+ years of experience in the academic field, his contributions to education and research have been instrumental in advancing technology and innovation. Currently serving as an Associate Professor at VIT Bhopal University. He is a valued supporter of our company, bringing his expertise in academia and research to drive progress and growth.",
    },
    // {
    //   name: "Dr. R. Vijaya Kumar",
    //   degree: "Ph.D.",
    //   institution: "ITES & IT Professional",
    //   experience: "25+ years in ITES/IT",
    //   image: "/assets/leadership/placeholder-1.png",
    //   bio: "A seasoned professional with over 25 years of experience in ITES and IT, including a decade in GIS for telecom projects and 15 years in software development. Skilled in image-to-vector conversion for US and UK clients, and proficient in system analysis, architecture, and full-stack development. Has led enterprise projects across healthcare, retail, telecom, and e-commerce, progressing from Developer to Technical Lead.",
    // },
 

  ];

  const teamMembers = [
    {
      name: "Mr. Pradeep Kumar",
      role: "Business Director",
      image:
        "/assets/Team/PradeepKumar.png",
      bio: "Experienced business leader driving strategic growth and innovation at amasQIS.ai. With a strong background in AI and business development, he bridges technology and strategy to achieve impactful results.",
      // email: "pradeep@amasqis.com",
      // linkedin: "https://linkedin.com/in/pradeepkumar",
      // instagram: "https://instagram.com/pradeepkumar",
      // whatsapp: "1234567890"
    },
    {
      name: "Anu",
      role: "Human Resources Executive",
      image: "/assets/Team/Humanresourcemanager.png",
      bio: "Expert in human resources management and talent acquisition, ensuring our team grows with the best professionals in the industry.",
      // email: "anu@amasqis.com",
      // linkedin: "https://linkedin.com/in/anu",
      // instagram: "https://instagram.com/anu",
      // whatsapp: "1234567890"
    },
    {
      name: "Sneha",
      role: "Project Management Executive",
      image:
        "/assets/Team/Projectmanagementandbusinessdevelopment.png",
      bio: "Experienced in project management and business development, driving strategic initiatives and client relationships.",
      // email: "sneha@amasqis.com",
      // linkedin: "https://linkedin.com/in/sneha",
      // instagram: "https://instagram.com/sneha",
      // whatsapp: "1234567891"
    },
    {
      name: "Vignesh",
      role: "GenAI Research Engineer",
      image:
        "/assets/Team/AIresearchengineer.png",
      bio: "Specialized in AI research and development, working on cutting-edge machine learning algorithms and innovative AI solutions.",
      // email: "vignesh@amasqis.com",
      // linkedin: "https://linkedin.com/in/vignesh",
      // instagram: "https://instagram.com/vignesh",
      // whatsapp: "1234567892"
    },
    {
      name: "Kathiravan",
      role: "GenAI Engineer",
      image:
        "/assets/Team/AIengineer.png",
      bio: "Expert AI engineer focused on implementing and deploying AI solutions across various industry applications.",
      // email: "kathiravan@amasqis.com",
      // linkedin: "https://linkedin.com/in/kathiravan",
      // instagram: "https://instagram.com/kathiravan",
      // whatsapp: "1234567893"
    },
    {
      name: "Sudhakar",
      role: "Full Stack Developer",
      image:
        "/assets/Team/fullstackdeveloper.png",
      bio: "Full-stack development specialist creating robust and scalable applications that power our AI platforms.",
      // email: "sudhakar@amasqis.com",
      // linkedin: "https://linkedin.com/in/sudhakar",
      // instagram: "https://instagram.com/sudhakar",
      // whatsapp: "1234567894"
    },
    {
      name: "Sowndharya",
      role: "UI/UX Engineer",
      image:
        "/assets/Team/UIUXEngineer.png",
      bio: "Creative UI/UX developer designing intuitive and engaging user experiences for our AI applications.",
      // email: "sowndharya@amasqis.com",
      // linkedin: "https://linkedin.com/in/sowndharya",
      // instagram: "https://instagram.com/sowndharya",
      // whatsapp: "1234567895"
    },
    {
      name: "Thangavel",
      role: "Web Developer",
      image:
        "/assets/Team/webdeveloper.png",
      bio: "Web development expert creating responsive and modern web applications that showcase our AI capabilities.",
      // email: "thangavel@amasqis.com",
      // linkedin: "https://linkedin.com/in/thangavel",
      // instagram: "https://instagram.com/thangavel",
      // whatsapp: "1234567896"
    },
  ];

  const coreValues = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Mission",
      description:
        "Our mission is to create AI-driven applications that enhance efficiency, solve real-world problems, and empower businesses globally. By leveraging Gen AI, Machine Learning, and advanced data processing, we aim to simplify operations, accelerate innovation, and enable our clients to achieve success in a rapidly evolving technological world and help various industries and universities.",
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Vision",
      description:
        "To revolutionize industries by delivering AI-powered solutions that tackle complex global challenges. Our vision is to grow amasQIS.ai into a unicorn, leading the innovation landscape with cutting-edge technologies in AI, LLMs, and IoT, while driving sustainable, impactful change across key sectors like real estate, energy, and water management.",
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Goals",
      description:
        "At amasQIS.ai, we're crafting bespoke AI tools to boost efficiency and spark sector-wide innovation. We scale up and spread impactful tech, with a keen eye on eco-friendly Green Hydrogen projects. Collaborating with academia and industry mavens, we're turning research into real-world applications. Plus, we're cultivating a diverse team of AI specialists, propelling us to the forefront of tech evolution.",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-15 bg-gradient-to-b from-background to-background/50 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#00cfff" fill-opacity="0.03"><path d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/></g></g></svg>')}")`,
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 neon-text">
              About <span className="amasqis-text">amasQIS.ai</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground text-center mb-8">
              amasQIS is a technology solutions company
              delivering enterprise-grade services in Artificial
              Intelligence (AI), Information Technology (IT),
              and Internet of Things (IoT). We enable
              organizations to build intelligent, scalable, and
              secure systems that drive efficiency, innovation,
              and sustainable digital transformation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-15 relative">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#0FFFA0" fill-opacity="0.05"><path d="M40 0C17.9 0 0 17.9 0 40s17.9 40 40 40 40-17.9 40-40S62.1 0 40 0zm0 72c-17.7 0-32-14.3-32-32S22.3 8 40 8s32 14.3 32 32-14.3 32-32 32z"/></g></g></svg>')}")`,
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Core Values / Foundation Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl font-bold mb-6 flex items-center">
                  <Target className="w-10 h-10 mr-4 text-primary" />
                  Core Values
                </h2>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-muted-foreground leading-relaxed text-lg text-jstify"
              >
                The principles that guide our innovation and ensure impact across diverse industries:
              </motion.p>

              <motion.ul
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                className="space-y-4"
              >
                {[
                  "Industry-Centric Innovation: Build AI solutions tailored to specific domains such as real estate, energy, water management, and manufacturing.",
                  "Operational Efficiency: Design intelligent systems that streamline workflows, reduce costs, and enhance productivity in sector-specific contexts.",
                  "Scalable Solutions: Deliver adaptable AI platforms that can evolve with industry needs and global challenges.",
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.5,
                      delay: 0.8 + 0.1 * index,
                    }}
                    viewport={{ once: true }}
                    className="flex items-start"
                  >
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-4 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      {item}
                    </span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

            {/* Mission Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="glass rounded-2xl p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/10 to-secondary/20" />

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                  className="relative"
                >
                  <ImageWithFallback
                    src="/assets/ourMission/ourMission.png"
                    alt="Our mission"
                    className="w-full h-64 lg:h-80 object-cover rounded-xl"
                  />

                  {/* Overlay content */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-xl flex items-end p-6">
                    <div className="text-white">
                      <h3 className="text-xl font-bold mb-2">
                        Transforming Industries
                      </h3>
                      <p className="text-sm opacity-90">
                        Through innovative AI solutions that
                        drive real-world impact
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Journey Section */}
      <section className="py-15 bg-gradient-to-b from-background/50 to-[#111111] relative hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#8A3FFC" fill-opacity="0.03"><path d="M50 0L100 50L50 100L0 50z"/></g></g></svg>')}")`,
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 flex items-center justify-center">
              <Calendar className="w-10 h-10 mr-4 text-primary" />
              Our Journey
            </h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
              From a startup vision to industry leader, here's
              how we've grown to serve clients worldwide with
              cutting-edge AI solutions
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="relative max-w-6xl mx-auto">
            {/* Timeline line - hidden on mobile, visible on desktop */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary via-accent to-secondary" />

            {journeyMilestones.map((milestone, index) => (
              <JourneyMilestone
                key={index}
                {...milestone}
                index={index}
              />
            ))}

            {/* Timeline continuation indicator */}
            <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 bottom-0 items-center justify-center">
              <motion.div
                className="bg-background px-4 py-2 rounded-full border border-primary/50 backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <span className="text-sm font-semibold bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                  Ongoing...
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Goals */}
      <section className="py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Our Foundation
            </h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
              The core principles that drive our innovation and
              guide our journey towards AI excellence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{
                  duration: 0.8,
                  delay: index * 0.2,
                }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <Card className="glass p-8 h-full text-left hover:border-primary/50 transition-all duration-500">
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center mb-6 text-black"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    {value.icon}
                  </motion.div>

                  <h3 className="text-2xl font-bold mb-4">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-left">
                    {value.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-15 bg-gradient-to-b from-background/50 to-[#111111] relative">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#8A3FFC" fill-opacity="0.05"><circle cx="20" cy="20" r="3"/><circle cx="20" cy="5" r="2"/><circle cx="20" cy="35" r="2"/><circle cx="5" cy="20" r="2"/><circle cx="35" cy="20" r="2"/></g></g></svg>')}")`,
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              Our History
            </h2>
            <div className="space-y-6 text-left">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-muted-foreground leading-relaxed text-lg text-justify"
              >
                amasQIS.ai, a trailblazer in AI innovation, has
                a rich history that mirrors its global reach.
                Founded by a team of experts in India, the
                company quickly expanded its operations to the
                UK, US, UAE, Oman, Germany, Malaysia and
                Singapore.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-muted-foreground leading-relaxed text-lg text-justify"
              >
                The inception of amasQIS.ai was marked by a
                vision to create transformative solutions that
                could address complex challenges across various
                industries. Leveraging international
                collaboration, the company has consistently
                pushed the boundaries of what's possible with
                AI, developing proprietary technologies that
                have set new standards in the field.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                className="text-muted-foreground leading-relaxed text-lg text-justify"
              >
                The journey of amasQIS.ai is a testament to the
                power of combining diverse expertise and the
                pursuit of excellence in the ever-evolving
                landscape of artificial intelligence.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-15 bg-[#111111] relative">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#0FFFA0" fill-opacity="0.03"><path d="M40 40L20 20v40h40V20z"/></g></g></svg>')}")`,
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 flex items-center justify-center">
              <Briefcase className="w-10 h-10 mr-4 text-primary" />
              Leadership & Team
            </h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
              Meet the visionary leaders driving amasQIS.ai's
              innovation and global expansion
            </p>
          </motion.div>

          {/* India Branch */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent inline-block font-['Comfortaa']">
                amasQIS.ai India
              </h3>
              <div className="h-1 w-32 mx-auto mt-3 bg-gradient-to-r from-transparent via-primary to-transparent rounded-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 max-w-[1600px] mx-auto">
              {leadershipByBranch.india.map((leader, index) => (
                <div
                  key={leader.name}
                  className="flex"
                >
                  <LeadershipMember
                    {...leader}
                    delay={index * 0.2}
                  />
                </div>
              ))}
            </div>
          </motion.div>

          {/* UK Branch */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-accent via-primary to-secondary bg-clip-text text-transparent inline-block font-['Comfortaa']">
                amasQIS.ai UK
              </h3>
              <div className="h-1 w-32 mx-auto mt-3 bg-gradient-to-r from-transparent via-accent to-transparent rounded-full" />
            </div>
            <div className="flex justify-center">
              <div className="w-full max-w-sm">
                <LeadershipMember {...leadershipByBranch.uk[0]} delay={0} />
              </div>
            </div>
          </motion.div>

          {/* Oman Branch */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-secondary via-accent to-primary bg-clip-text text-transparent inline-block font-['Comfortaa']">
                amasQIS.ai Oman
              </h3>
              <div className="h-1 w-32 mx-auto mt-3 bg-gradient-to-r from-transparent via-secondary to-transparent rounded-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {leadershipByBranch.oman.map((leader, index) => (
                <LeadershipMember key={leader.name} {...leader} delay={index * 0.2} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industry Pioneers */}
      <section className="py-15 relative overflow-hidden hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#00cfff" fill-opacity="0.05"><path d="M30 0L60 30L30 60L0 30z"/></g></g></svg>')}")`,
          }}
        />

        <div className="max-w-7xl mx-auto pAx-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 flex items-center justify-center">
              <Beaker className="w-10 h-10 mr-4 text-primary" />
              Industry Pioneers
            </h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
              Expert advisors and pioneers who guide our
              innovation in emerging technologies
            </p>
          </motion.div>

          {industryPioneers.length <= 3 ? (
            <div className="flex flex-wrap justify-center gap-8 max-w-7xl mx-auto items-stretch">
              {industryPioneers.map((pioneer, index) => (
                <div
                  key={`${pioneer.name}-${index}`}
                  className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] max-w-md flex"
                >
                  <TeamMember
                    {...pioneer}
                    delay={index * 0.2}
                  />
                </div>
              ))}
            </div>
          ) : (
            <IndustryPioneersSlider
              pioneers={industryPioneers}
            />
          )}
        </div>
      </section>

      {/* Academic Pioneers */}
      <section className="py-20 bg-gradient-to-b from-background via-secondary/5 to-background relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-32 h-32 bg-accent/20 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-20 right-10 w-40 h-40 bg-primary/20 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          />
          <div
            className="absolute top-1/2 left-1/3 w-24 h-24 bg-secondary/20 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "2s" }}
          />
        </div>

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#0FFFA0" fill-opacity="0.4"><path d="M0 0h40v40H0V0zm40 40h40v40H40V40z"/></g></g></svg>')}")`,
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="inline-block mb-6"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-accent to-primary blur-2xl opacity-50" />
                <GraduationCap className="w-16 h-16 text-accent relative z-10" />
              </div>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-accent via-primary to-secondary bg-clip-text text-transparent">
              Academic Pioneers
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Distinguished scholars and researchers bringing
              academic excellence and innovation to our mission
            </p>
          </motion.div>

          <AcademicPioneersShowcase
            pioneers={academicPioneers}
          />
        </div>
      </section>

      {/* Our Talented Associates Section */}
      <section className="py-15 relative hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#0FFFA0" fill-opacity="0.05"><path d="M30 0L60 30L30 60L0 30z"/></g></g></svg>')}")`,
          }}
        />

        <div className="container mx-auto  sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 flex items-center justify-center">
              <Users className="w-10 h-10 mr-4 text-primary" />
              Our Talented Associates
            </h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
              Meet the brilliant minds behind amasQIS.ai,
              dedicated professionals driving innovation in AI
              and technology
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <ThreeDTeamCarousel teamMembers={teamMembers} />
          </motion.div>
        </div>
      </section>
    </div>
  );
}